import { createContext, useContext, useState, useLayoutEffect, useMemo } from "react";
import { createTheme, ThemeProvider as MuiThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { lightTokens, darkTokens, CSS_VAR_MAP } from "../theme/tokens";

const ThemeContext = createContext();

export const useTheme = () => useContext(ThemeContext);
export const useDesignTokens = () => useContext(ThemeContext).tokens;

function buildPalette(mode, tokens) {
    return {
        mode,
        primary: { main: tokens.primary, dark: tokens.primaryDark },
        error: { main: tokens.errorBright, dark: tokens.error },
        secondary: { main: "#ff4081" },
        background: {
            default: tokens.bgBody,
            paper: tokens.bgPaper,
        },
        text: {
            primary: tokens.textPrimary,
            secondary: tokens.textSecondary,
        },
    };
}

export function ThemeProvider({ children }) {
    const [mode, setMode] = useState(() => {
        const saved = localStorage.getItem("theme");
        return saved === "dark" ? "dark" : "light";
    });

    const tokens = mode === "dark" ? darkTokens : lightTokens;

    useLayoutEffect(() => {
        localStorage.setItem("theme", mode);
        const root = document.documentElement;
        root.setAttribute("data-theme", mode);
        Object.entries(CSS_VAR_MAP).forEach(([key, cssVar]) => {
            root.style.setProperty(cssVar, tokens[key]);
        });
    }, [mode, tokens]);

    const theme = useMemo(
        () =>
            createTheme({
                palette: buildPalette(mode, tokens),
                typography: {
                    fontFamily: "'Segoe UI', 'Roboto', 'Helvetica Neue', sans-serif",
                },
            }),
        [mode, tokens]
    );

    const toggleTheme = () => setMode((prev) => (prev === "light" ? "dark" : "light"));

    return (
        <ThemeContext.Provider value={{ mode, toggleTheme, tokens }}>
            <MuiThemeProvider theme={theme}>
                <CssBaseline />
                {children}
            </MuiThemeProvider>
        </ThemeContext.Provider>
    );
}
